import { useState } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import { Stack, AppBar, Toolbar, IconButton, Button } from '@mui/material';
import { useQuery } from 'react-query';
// utils
import { bgBlur } from '../../../utils/cssStyles';
// hooks
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
// config
import { HEADER, NAV } from '../../../config-global';
// components
import Logo from '../../../components/logo';
import Iconify from '../../../components/iconify';
import { useSettingsContext } from '../../../components/settings';
//
import AccountPopover from './AccountPopover';
import ThemeSwitch from '../../../components/theme-switch/themeSwitch';

import axiosAPI from '../../../utils/axios';

import AddFundsPopup from '../../../components/addFunds/addFunds';
import InstallPopupPWA from '../../../components/PWAFeatures/installPopupPWA';
import useMainStore from '../../../store/store';
import AIManager from '../../../components/aiRecs/aiRecs';
import { useAuthContext } from '../../../auth/useAuthContext';

// ----------------------------------------------------------------------

type Props = {
  onOpenNav?: VoidFunction;
};

export default function Header({ onOpenNav }: Props) {
  const { user } = useAuthContext();
  const showRecs = user?.role === 'sysAdmin' || user?.role === 'advManager';

  const theme = useTheme();
  const showInstallPWA = useMainStore((s: any) => s.showInstallPWA);

  const [showAddFunds, setShowAddFunds] = useState(false);

  const { themeLayout } = useSettingsContext();

  const isNavHorizontal = themeLayout === 'horizontal';

  const isNavMini = themeLayout === 'mini';

  const isDesktop = useResponsive('up', 'lg');

  const isOffset = useOffSetTop(HEADER.H_DASHBOARD_DESKTOP) && !isNavHorizontal;

  const { isLoading: isProfileLoading, data: profileData } = useQuery('users/profile', () =>
    axiosAPI.get('/users/profile').then((res) => res.data.result)
  );

  const balance = profileData?.balance != null ? profileData?.balance.toFixed(2) : undefined;
  const showBalance = balance != null;

  const renderContent = (
    <>
      {isDesktop && isNavHorizontal && <Logo sx={{ mr: 2.5 }} />}

      {!isDesktop && (
        <IconButton onClick={onOpenNav} sx={{ mr: 1, color: 'text.primary' }}>
          <Iconify icon="eva:menu-2-fill" />
        </IconButton>
      )}

      {showInstallPWA && <InstallPopupPWA />}

      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 1.5, sm: 2.5 }}
      >
        <ThemeSwitch />
        {showBalance && (
          <Button variant="contained" color="info" onClick={() => setShowAddFunds(true)}>
            Add Funds
          </Button>
        )}
        {showBalance && <Stack sx={{ color: 'text.primary', fontWeight: 500 }}>${balance}</Stack>}
        <AccountPopover />
        {showRecs && <AIManager />}
      </Stack>
    </>
  );

  return (
    <AppBar
      sx={{
        boxShadow: 'none',
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(isDesktop && {
          width: `calc(100% - ${NAV.W_DASHBOARD + 1}px)`,
          height: HEADER.H_DASHBOARD_DESKTOP,
          ...(isOffset && {
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
          }),
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: 'background.default',
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
            borderBottom: `dashed 1px ${theme.palette.divider}`,
          }),
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_DASHBOARD_MINI + 1}px)`,
          }),
        }),
      }}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
        }}
      >
        {renderContent}
      </Toolbar>
      <AddFundsPopup show={showAddFunds} close={() => setShowAddFunds(false)} />
    </AppBar>
  );
}
